import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PaginatedResponse, TemplateChecklist, TemplateItem } from '../types'

export interface TemplateCreateInput {
  nome: string
  tipo_os: string
  tipo_equipamento?: string
  descricao?: string
  ativo?: boolean
}

export interface TemplateItemCreateInput {
  ordem: number
  pergunta: string
  tipo_resposta: 'sim_nao' | 'numerico' | 'texto'
  obrigatorio?: boolean
  requer_evidencia?: boolean
  unidade?: string
  valor_min?: string | null
  valor_max?: string | null
  ajuda?: string
  depende_de?: string | null
  condicao_valor?: string
}

interface TemplateListParams {
  tipo_os?: string
  tipo_equipamento?: string
  ativo?: boolean
}

export function useTemplateList(params?: TemplateListParams) {
  return useQuery({
    queryKey: ['templates-checklist', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<TemplateChecklist>>(
        '/checklists/templates/',
        { params },
      )
      return r.data
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useTemplateDetalheCompleto(id: string) {
  return useQuery({
    queryKey: ['templates-checklist', id],
    queryFn: async () => {
      const r = await api.get<TemplateChecklist>(`/checklists/templates/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

function clean(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  Object.entries(o).forEach(([k, v]) => {
    if (v === '' || v === undefined) return
    out[k] = v
  })
  return out
}

export function useTemplateCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: TemplateCreateInput) => {
      const r = await api.post<TemplateChecklist>(
        '/checklists/templates/',
        clean(data as unknown as Record<string, unknown>),
      )
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates-checklist'] }),
  })
}

export function useTemplateUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<TemplateCreateInput>) => {
      const r = await api.patch<TemplateChecklist>(
        `/checklists/templates/${id}/`,
        clean(data as Record<string, unknown>),
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates-checklist'] })
      qc.invalidateQueries({ queryKey: ['templates-checklist', id] })
    },
  })
}

export function useTemplateItemCreate(templateId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: TemplateItemCreateInput) => {
      const r = await api.post<TemplateItem>(
        `/checklists/templates/${templateId}/itens/`,
        clean(data as unknown as Record<string, unknown>),
      )
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates-checklist', templateId] }),
  })
}

export function useTemplateItemDelete(templateId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => api.delete(`/checklists/itens/${itemId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates-checklist', templateId] }),
  })
}

// ──────────────────────────────────────────────────────────
// Bulk import de Templates (multi-aba)
// ──────────────────────────────────────────────────────────
export interface TemplateImportColuna {
  coluna: number
  header_planilha: string
  campo_sistema: string | null
  reconhecida: boolean
}

export interface TemplateImportLinha {
  linha: number
  aba: string
  acao: 'criar' | 'ignorar_duplicado' | 'erro'
  erro: string | null
  dados: Record<string, unknown>
}

export interface TemplateImportResultado {
  templates: {
    total: number
    a_criar: number
    duplicados: number
    erros: number
    linhas: TemplateImportLinha[]
  }
  itens: {
    total: number
    a_criar: number
    erros: number
    linhas: TemplateImportLinha[]
  }
  mapeamento_templates: TemplateImportColuna[]
  mapeamento_itens: TemplateImportColuna[]
  dry_run: boolean
  commit?: { templates_criados: number; itens_criados: number; ids_templates: string[] }
}

export function useTemplateImportar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, dry_run }: { arquivo: File; dry_run: boolean }) => {
      const fd = new FormData()
      fd.append('arquivo', arquivo)
      fd.append('dry_run', dry_run ? 'true' : 'false')
      const r = await api.post<TemplateImportResultado>(
        '/checklists/templates/importar/',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return r.data
    },
    onSuccess: (data) => {
      if (!data.dry_run) qc.invalidateQueries({ queryKey: ['templates-checklist'] })
    },
  })
}

export function downloadTemplateChecklists() {
  return api
    .get('/checklists/templates/importar/template/', { responseType: 'blob' })
    .then((r) => {
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'template-checklists-pmoc123.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    })
}
